import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Box } from '@mui/material';
import { useMobileContext } from '../../contexts/MobileContext';
import PageShell from '../../components/Layout/PageShell';
import CarInsurancePage from '../../components/SubPages/CarInsurancePage';
import FuneralInsurancePage from '../../components/SubPages/FuneralInsurancePage';
import LifeInsurancePage from '../../components/SubPages/LifeInsurancePage';
import LifeInsuranceCompaniesPage from '../../components/SubPages/LifeInsuranceCompaniesPage';
import CarInsuranceCompaniesPage from '../../components/SubPages/CarInsuranceCompaniesPage';
import MedicalAidCompaniesPage from '../../components/SubPages/MedicalAidCompaniesPage';
import MotorWarrantyPage from '../../components/SubPages/MotorWarrantyPage';
import PetInsurancePage from '../../components/SubPages/PetInsurancePage';
import TravelInsurancePage from '../../components/SubPages/TravelInsurancePage';
import FooterComponent from '../../components/HomePage/Footer/FooterComponent';
import FooterMobileComponent from '../../components/HomePage/Footer/FooterMobileComponent';
import { useLocation } from 'react-router-dom';
import PopupModalComponent from '../../components/PopupModal/PopupModalComponent';
import { updateFormValuesFromApi } from '../../utils/returningUserApiMapper';
import { persistCrossSellQuotes } from '../../utils/carouselQuoteHelpers';
import SeoTagsManager from '../../utils/SeoTagsManager';
import { normalizePathname } from '../../utils/productNavigation';
import { isBotUserAgent } from '../../utils/isBot';
import LoadingDots from '../../components/LoadingDots/LoadingDots';
import BreadcrumbComponent from '../../components/Breadcrumb/BreadcrumbComponent';
import { BREADCRUMBS, getBreadcrumbs } from '../../utils/breadcrumbConfig';

import {
  getPersonPreFill,
  profileSendOTPViaSMS,
  profileSendOTPViaEmail,
  profileValidateOTP,
  profileRetrievePerson,
  profileRetrievePropertyDetails,
  profileRetrievePetDetails,
  profileRetrieveVehicles,
  profileRetrieveVehicleDrivers,
  getCrossSellQuotes,
} from '../../api/apiServices';
import { syncAccountData } from '../../utils/accountDataCache';
import { mergePrefillIntoFormValues } from '../../utils/questionEnginePrefillHelpers';
import {
  isValidatedWithin,
  isBypassedWithin,
  isValidSaContactNumber,
  persistAuthSession as persistStoredAuthSession,
  persistReturnUserDetails as persistStoredReturnUserDetails,
  buildPolicyHolderName,
  clearPersistedAuthSession,
  ENABLE_OTP_SMS,
} from '../../utils/questionEngineOtpHelpers';
import MedicalAidPage from '../../components/SubPages/MedicalAidPage';
import HomePage from '../../components/SubPages/HomePage';
import DentsAndScratchesPage from '../../components/SubPages/DentsAndScratchesPage';
import MedicalGapCoverPage from '../../components/SubPages/MedicalGapCoverPage';
import MedicalInsurancePage from '../../components/SubPages/MedicalInsurancePage';
import BuildingInsurancePage from '../../components/SubPages/BuildingInsurancePage';
import BusinessInsurancePage from '../../components/SubPages/BusinessInsurancePage';
import TyreAndRimPage from '../../components/SubPages/TyreAndRimPage';
import VehicleTrackingPage from '../../components/SubPages/VehicleTrackingPage';
import ServicePlansPage from '../../components/SubPages/ServicePlansPage';
import FibrePage from '../../components/SubPages/FibrePage';
import MotorCyclePage from '../../components/SubPages/MotorCyclePage';
import HomeContentPage from '../../components/SubPages/HomeContentPage';
import ComboInsurancePage from '../../components/SubPages/ComboInsurancePage';
import PersonalLoanPage from '../../components/SubPages/PersonalLoanPage';
import DebtCounsellingPage from '../../components/SubPages/DebtCounsellingPage';
import DebtConsolidationPage from '../../components/SubPages/DebtConsolidationPage';
import CommercialPropertyPage from '../../components/SubPages/CommercialPropertyPage';
import EmployeeBenefitPage from '../../components/SubPages/EmployeeBenefitPage';
import BusinessLegalCoverPage from '../../components/SubPages/BusinessLegalCoverPage';
import EventLiabilityCoverPage from '../../components/SubPages/EventLiabilityCoverPage';
import ProfessionalLiabilityPage from '../../components/SubPages/ProfessionalLiabilityPage';
import InsurancePage from '../../components/SubPages/InsurancePage';
import MotorPage from '../../components/SubPages/MotorPage';
import HealthPage from '../../components/SubPages/HealthPage';
import MoneyPage from '../../components/SubPages/MoneyPage';
import InternetPage from '../../components/SubPages/InternetPage';

// ------------------ helpers ------------------

const safeParse = (raw, fb = null) => {
  try {
    return raw ? JSON.parse(raw) : fb;
  } catch {
    return fb;
  }
};

const maskCell = (cell) => {
  if (!cell) return '';
  const clean = String(cell).replace(/\D/g, ''); // remove spaces, +27, etc.
  // Only mask if we have at least 10 digits
  return clean.replace(
    /(\d)(\d)(\d)(\d{3})(\d{2})(\d{2})/,
    (_, a, b, c, d, e, f) => `${a}•${c} ••• ••${f}`
  );
};

const maskEmail = (email) => {
  if (!email) return '';
  const [local, domain] = String(email).split('@');
  if (!domain) return email;
  const visible =
    local.length <= 2
      ? local
      : local[0] +
        '•'.repeat(Math.min(local.length - 2, 4)) +
        local[local.length - 1];
  return `${visible}@${domain}`;
};

const normalizeCellNumber = (cell) => {
  if (!cell) return '';
  let s = String(cell).trim().replace(/\s+/g, ''); // remove spaces
  if (s.startsWith('+27')) s = '0' + s.slice(3); // +27… → 0…
  return s;
};

const buildRequestOtpModal = (
  firstNameHint,
  { via = 'phone', prefill = '', returningUser = false } = {}
) => ({
  title: firstNameHint
    ? `Is that you, ${firstNameHint}?`
    : "Let's get you signed in",
  content: "Let's send you an OTP so we can get your quotes faster",
  type: 'otp-request',
  questions: [
    {
      layout: 'column',
      ...(via === 'email'
        ? {
            email_address: {
              type: 'text-field',
              orderNumber: 0,
              value: prefill,
              placeholder: 'Enter your email address',
              validation: {
                regex: '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$',
                message: 'Please enter a valid email address',
              },
            },
          }
        : {
            contact_number: {
              type: 'text-field',
              orderNumber: 0,
              value: prefill,
              onlyNumbers: true,
              placeholder: 'Enter your cell number',
              validation: {
                regex: '^0\\d{9}$',
                message: 'Please enter a valid South African cellphone number',
              },
            },
          }),
    },
  ],
  buttons: returningUser
    ? [
        { index: 0, id: 'send-otp', type: 'primary-green', text: 'Send OTP' },
        {
          index: 1,
          id: 'no-thats-not-me',
          type: 'secondary-gray',
          text: "No, that's not me",
        },
      ]
    : via === 'email'
      ? [
          { index: 0, id: 'send-otp', type: 'primary-green', text: 'Send OTP' },
          // SMS is disabled — don't offer a path back to the phone flow.
          ...(ENABLE_OTP_SMS
            ? [
                {
                  index: 1,
                  id: 'use-phone-instead',
                  type: 'secondary-gray',
                  text: 'Use cell number instead',
                },
              ]
            : []),
        ]
      : [
          { index: 0, id: 'send-otp', type: 'primary-green', text: 'Send OTP' },
          {
            index: 0,
            id: 'first-time-quoting',
            type: 'secondary-gray',
            text: "It's My First Time Quoting",
          },
        ],
});

const buildEnterOtpModal = (cell, options = {}) => {
  const { returningUser = false } =
    typeof options === 'object' && options !== null ? options : {};
  return {
    title: "Let's keep your information safe",
    subtitle:
      'Verification helps protect and secure your personal information.',
    content: `We've sent a verification code to\n${maskCell(cell)}`,
    type: 'otp-input',
    questions: [
      {
        layout: 'column',
        otp_code: {
          type: 'otp-input',
          orderNumber: 0,
          length: 4,
          validation: {
            required: true,
            message: 'Please enter the 4-digit code',
          },
        },
      },
    ],
    buttons: returningUser
      ? [
          {
            index: 1,
            id: 'no-thats-not-me',
            type: 'third-gray',
            text: "That's not me",
          },
        ]
      : [
          {
            index: 1,
            id: 'guest-quote',
            type: 'third-gray',
            text: 'Continue without verifying',
            bold: true,
          },
          {
            index: 2,
            id: 'use-email-instead',
            type: 'third-gray',
            text: 'Send code by email instead',
          },
        ],
  };
};

const buildEnterEmailOtpModal = (email, options = {}) => {
  const { returningUser = false } =
    typeof options === 'object' && options !== null ? options : {};
  return {
    title: "Let's keep your information safe",
    subtitle:
      'Verification helps protect and secure your personal information.',
    content: `We've sent a verification code to\n${maskEmail(email)}`,
    type: 'otp-input',
    questions: [
      {
        layout: 'column',
        otp_code: {
          type: 'otp-input',
          orderNumber: 0,
          length: 4,
          validation: {
            required: true,
            message: 'Please enter the 4-digit code',
          },
        },
      },
    ],
    buttons: returningUser
      ? [
          {
            index: 1,
            id: 'no-thats-not-me',
            type: 'third-gray',
            text: "That's not me",
          },
        ]
      : [
          {
            index: 1,
            id: 'guest-quote',
            type: 'third-gray',
            text: 'Continue without verifying',
            bold: true,
          },
        ],
  };
};

function Home() {
  const isMobile = useMobileContext();
  const { pathname } = useLocation();
  const normalizedPathname = normalizePathname(pathname);
  const heroGateKey = `${normalizedPathname}:${
    isMobile.isDesktop ? 'desktop' : 'mobile'
  }`;

  const [otpSubmitting, setOtpSubmitting] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpResetCounter, setOtpResetCounter] = useState(0);
  const [otpViaEmail, setOtpViaEmail] = useState(false);

  // Wait for the hero image before revealing, so real users don't see it pop in.
  // Bots (crawlers, Lighthouse/PageSpeed, prerender) skip the wait: they don't
  // perceive jank, and gating behind a spinner only delays their measured LCP.
  const skipHeroGate = useMemo(() => isBotUserAgent(), []);
  const [loadedHeroKey, setLoadedHeroKey] = useState('');

  const showHomePage = normalizedPathname === '/';
  const showCarInsurancePage = normalizedPathname === '/car-insurance-quote';
  const showFuneralInsurancePage =
    normalizedPathname === '/funeral-insurance-quote';
  const showLifeInsurancePage = normalizedPathname === '/life-insurance-quote';
  const showMedicalAidPage = normalizedPathname === '/medical-aid-quote';
  const showLifeInsuranceCompaniesPage =
    normalizedPathname === '/life-insurance-quote/life-insurance-companies';
  const showCarInsuranceCompaniesPage =
    normalizedPathname === '/car-insurance-quote/companies';
  const showMedicalAidCompaniesPage =
    normalizedPathname === '/medical-aid-quote/medical-aid-companies';
  const showPetInsurancePage = normalizedPathname === '/pet-insurance-quote';
  const showMotorWarranty = normalizedPathname === '/motor-warranty';
  const showDentsAndScratches =
    normalizedPathname === '/dents-and-scratches-quote';
  const showTravelInsurance = normalizedPathname === '/travel-insurance';
  const showGapCover = normalizedPathname === '/medical-gap-cover-quote';
  const showMedicalInsurance =
    normalizedPathname === '/medical-insurance-quotes';
  const showBuildingInsurance =
    normalizedPathname === '/buildings-insurance-quote';
  const showBusinessInsurance =
    normalizedPathname === '/business-insurance-quote';
  const showTyreAndRim = normalizedPathname === '/tyre-and-rim-guard';
  const showVehicleTracking = normalizedPathname === '/vehicle-tracking-quote';
  const showServicePlans = normalizedPathname === '/service-plan-quotes';
  const showFibre = normalizedPathname === '/compare-fibre-deals';
  const showMotorCycle = normalizedPathname === '/motorcycle-insurance-quote';
  const showHomeContent = normalizedPathname === '/home-insurance-quote';
  const showComboInsurancePage =
    normalizedPathname === '/online-insurance-quote';
  const showPersonalLoan = normalizedPathname === '/personal-loans';
  const showDebtCounselling = normalizedPathname === '/debt-counselling';
  const showDebtConsolidation =
    normalizedPathname === '/debt-consolidation-loans';
  const showCommercialProperty =
    normalizedPathname ===
    '/business-insurance-quote/car-and-commercial-property-insurance';
  const showEmployeeBenefits =
    normalizedPathname === '/employee-benefits-quote';
  const showBusinessLegalCover =
    normalizedPathname === '/business-insurance-quote/business-legal-cover';
  const showProfessionalLiability =
    normalizedPathname ===
    '/business-insurance-quote/professional-liability-insurance-cover';
  const showEventLiabilityCover =
    normalizedPathname ===
    '/business-insurance-quote/event-liability-insurance';
  const showInsuranceCategory = normalizedPathname === '/insurance';
  const showMotorCategory = normalizedPathname === '/motor';
  const showHealthCategory = normalizedPathname === '/health';
  const showMoneyCategory = normalizedPathname === '/money';
  const showInternetCategory = normalizedPathname === '/internet';

  // ---- OTP flow state
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [otpModalData, setOtpModalData] = useState(null);
  const [modalStage, setModalStage] = useState('idle'); // 'idle' | 'request' | 'enter'

  // cache frequently used localStorage values
  const formValues = useMemo(
    () => safeParse(localStorage.getItem('formValues'), {}),
    []
  );
  const authSession = useMemo(
    () => safeParse(localStorage.getItem('authSession'), {}),
    []
  );

  const firstNameHint = useMemo(() => {
    // if you store name in formValues, feel free to adapt this
    return formValues?.FirstName || formValues?.firstName || '';
  }, [formValues]);

  const cellFromForm = useMemo(
    () =>
      formValues?.cellNumber ||
      formValues?.phone ||
      formValues?.CellNumber ||
      '',
    [formValues]
  );

  const applyIdPrefillToStoredFormValues = useCallback(
    async (returnUserDetails, baseFormValues = {}) => {
      const idNumber = String(
        returnUserDetails?.IDNumber || returnUserDetails?.idNumber || ''
      ).trim();

      if (!idNumber) return baseFormValues;

      const anchor = localStorage.getItem('idNumberAnchor') || '';
      if (anchor === idNumber) return baseFormValues;

      try {
        const prefillData = await getPersonPreFill(idNumber);
        const nextFormValues = mergePrefillIntoFormValues(
          prefillData,
          baseFormValues
        );

        localStorage.setItem('formValues', JSON.stringify(nextFormValues));
        return nextFormValues;
      } catch (error) {
        return baseFormValues;
      } finally {
        localStorage.setItem('idNumberAnchor', idNumber);
      }
    },
    []
  );

  // ---------------- core actions ----------------

  const fetchReturnUserDataSilently = useCallback(
    async (identifier, { via = 'phone' } = {}) => {
      try {
        const isEmail = via === 'email';
        const lookupId = isEmail
          ? String(identifier || '').trim()
          : normalizeCellNumber(identifier);

        getCrossSellQuotes(lookupId)
          .then(persistCrossSellQuotes)
          .catch(() => {});

        const [personRes, vehiclesRes, driversRes, propertiesRes] =
          await Promise.all([
            profileRetrievePerson(lookupId),
            profileRetrieveVehicles(lookupId),
            profileRetrieveVehicleDrivers(lookupId),
            profileRetrievePropertyDetails(lookupId),
          ]);
        const petsRes = await profileRetrievePetDetails(lookupId).catch(
          () => null
        );

        const res = {
          ...personRes,
          Vehicles: vehiclesRes?.Vehicles || [],
          Drivers: driversRes?.Drivers || [],
          Properties: propertiesRes || null,
          Pets: petsRes?.Pets || [],
        };

        const mergedFormValues = updateFormValuesFromApi(res);
        await applyIdPrefillToStoredFormValues(res, mergedFormValues || {});
        persistStoredReturnUserDetails(res);

        const resolvedCellNumber = isEmail
          ? normalizeCellNumber(personRes?.CellNumber || '')
          : lookupId;

        persistStoredAuthSession({
          ...(isEmail ? { email: lookupId } : {}),
          ...(resolvedCellNumber ? { cellNumber: resolvedCellNumber } : {}),
          profileName: buildPolicyHolderName(res),
        });
        void syncAccountData(resolvedCellNumber || lookupId, {
          seedPerson: personRes,
          seedVehicles: vehiclesRes,
          seedDrivers: driversRes,
          seedProperties: propertiesRes,
          seedPets: petsRes,
        }).catch(() => {});
        return true;
      } catch (err) {
        return false;
      }
    },
    [applyIdPrefillToStoredFormValues]
  );

  const tryGetReturnUserDetails = useCallback(
    async (cell, otp) => {
      try {
        const normalized = normalizeCellNumber(cell);

        const validateResult = await profileValidateOTP({
          cellNumber: normalized,
          otp,
        });
        const validatedEntry = Array.isArray(validateResult)
          ? validateResult[0]
          : null;
        if (!validatedEntry?.Validated) {
          throw new Error('OTP validation failed');
        }
        // Store JWT immediately so subsequent profile calls are authenticated
        persistStoredAuthSession({
          jwtToken: validatedEntry.JWTToken,
          jwtValidTo: validatedEntry.ValidToDateUTC,
          otpVia: 'sms',
        });

        getCrossSellQuotes(normalized)
          .then(persistCrossSellQuotes)
          .catch(() => {});

        const [personRes, vehiclesRes, driversRes, propertiesRes] =
          await Promise.all([
            profileRetrievePerson(normalized),
            profileRetrieveVehicles(normalized),
            profileRetrieveVehicleDrivers(normalized),
            profileRetrievePropertyDetails(normalized),
          ]);
        const petsRes = await profileRetrievePetDetails(normalized).catch(
          () => null
        );

        const res = {
          ...personRes,
          Vehicles: vehiclesRes?.Vehicles || [],
          Drivers: driversRes?.Drivers || [],
          Properties: propertiesRes || null,
          Pets: petsRes?.Pets || [],
        };

        const mergedFormValues = updateFormValuesFromApi(res);
        await applyIdPrefillToStoredFormValues(res, mergedFormValues || {});
        persistStoredReturnUserDetails(res);
        persistStoredAuthSession({
          cellNumber: normalized,
          otp,
          validatedAt: Date.now(),
          profileName: buildPolicyHolderName(res),
        });
        void syncAccountData(normalized, {
          seedPerson: personRes,
          seedVehicles: vehiclesRes,
          seedDrivers: driversRes,
          seedProperties: propertiesRes,
          seedPets: petsRes,
        }).catch(() => {});
        setOtpModalOpen(false);
        setOtpError('');
        setModalStage('idle');
        return true;
      } catch (err) {
        // Stay on the ENTER-OTP modal, clear inputs, show error
        setOtpError('Invalid code. Please try again.');
        setOtpResetCounter((n) => n + 1);
        setOtpModalData(buildEnterOtpModal(cell, firstNameHint));
        setModalStage('enter');
        setOtpModalOpen(true);
        return false;
      }
    },
    [applyIdPrefillToStoredFormValues, firstNameHint]
  );

  const tryGetReturnUserDetailsByEmail = useCallback(
    async (email, otp) => {
      try {
        const trimmedEmail = String(email || '').trim();

        const validateResult = await profileValidateOTP({
          email: trimmedEmail,
          otp,
        });
        const validatedEntry = Array.isArray(validateResult)
          ? validateResult[0]
          : null;
        if (!validatedEntry?.Validated) {
          throw new Error('OTP validation failed');
        }
        persistStoredAuthSession({
          jwtToken: validatedEntry.JWTToken,
          jwtValidTo: validatedEntry.ValidToDateUTC,
          otpVia: 'email',
        });

        getCrossSellQuotes(trimmedEmail)
          .then(persistCrossSellQuotes)
          .catch(() => {});

        const [personRes, vehiclesRes, driversRes, propertiesRes] =
          await Promise.all([
            profileRetrievePerson(trimmedEmail),
            profileRetrieveVehicles(trimmedEmail),
            profileRetrieveVehicleDrivers(trimmedEmail),
            profileRetrievePropertyDetails(trimmedEmail),
          ]);
        const petsRes = await profileRetrievePetDetails(trimmedEmail).catch(
          () => null
        );

        const res = {
          ...personRes,
          Vehicles: vehiclesRes?.Vehicles || [],
          Drivers: driversRes?.Drivers || [],
          Properties: propertiesRes || null,
          Pets: petsRes?.Pets || [],
        };

        const mergedFormValues = updateFormValuesFromApi(res);
        await applyIdPrefillToStoredFormValues(res, mergedFormValues || {});
        persistStoredReturnUserDetails(res);
        persistStoredAuthSession({
          email: trimmedEmail,
          otp,
          validatedAt: Date.now(),
          profileName: buildPolicyHolderName(res),
        });
        void syncAccountData(trimmedEmail, {
          seedPerson: personRes,
          seedVehicles: vehiclesRes,
          seedDrivers: driversRes,
          seedProperties: propertiesRes,
          seedPets: petsRes,
        }).catch(() => {});
        setOtpModalOpen(false);
        setOtpError('');
        setModalStage('idle');
        setOtpViaEmail(false);
        return true;
      } catch (err) {
        setOtpError('Invalid code. Please try again.');
        setOtpResetCounter((n) => n + 1);
        const session =
          safeParse(localStorage.getItem('authSession'), {}) || {};
        const currentEmail =
          session?.email ||
          safeParse(localStorage.getItem('formValues'), {})?.email ||
          safeParse(localStorage.getItem('formValues'), {})?.EmailAddress ||
          '';
        setOtpModalData(buildEnterEmailOtpModal(currentEmail));
        setModalStage('enter');
        setOtpModalOpen(true);
        setOtpViaEmail(true);
        return false;
      }
    },
    [applyIdPrefillToStoredFormValues]
  );

  const sendOtpTo = useCallback(
    async (cell) => {
      const normalized = normalizeCellNumber(cell);
      await profileSendOTPViaSMS(normalized, firstNameHint);
      persistStoredAuthSession({
        cellNumber: normalized,
        otp: null,
        requestedAt: Date.now(),
      });
      setOtpModalData(buildEnterOtpModal(normalized, firstNameHint));
      setModalStage('enter');
      setOtpModalOpen(true);
      setOtpViaEmail(false);
    },
    [firstNameHint]
  );

  const sendOtpToEmail = useCallback(
    async (email) => {
      const trimmedEmail = String(email || '').trim();
      await profileSendOTPViaEmail(trimmedEmail, firstNameHint);
      persistStoredAuthSession({
        email: trimmedEmail,
        otp: null,
        requestedAt: Date.now(),
      });
      setOtpError('');
      setOtpModalData(buildEnterEmailOtpModal(trimmedEmail));
      setModalStage('enter');
      setOtpModalOpen(true);
      setOtpViaEmail(true);
    },
    [firstNameHint]
  );

  // ---------------- modal handlers ----------------

  const handleOtpModalClose = useCallback(() => {
    setOtpModalOpen(false);
    setOtpViaEmail(false);
  }, []);

  const handleOtpModalAction = useCallback(
    async (id, formState) => {
      if (id === 'send-otp') {
        if (modalStage === 'request-email') {
          const enteredEmail = String(formState?.email_address || '').trim();
          if (!enteredEmail) return;
          if (otpSubmitting) return;
          try {
            setOtpError('');
            setOtpSubmitting(true);
            await sendOtpToEmail(enteredEmail);
          } finally {
            setOtpSubmitting(false);
          }
          return;
        }

        const enteredCell =
          formState?.contact_number ||
          cellFromForm ||
          authSession?.cellNumber ||
          '';

        if (!enteredCell.trim()) return;
        if (!isValidSaContactNumber(enteredCell)) return;
        if (otpSubmitting) return;
        try {
          setOtpError('');
          setOtpSubmitting(true);
          await sendOtpTo(enteredCell);
        } finally {
          setOtpSubmitting(false);
        } // ← normalizes internally
        return;
      }

      if (id === 'first-time-quoting') {
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = '/';
        return;
      }

      if (id === 'use-email-instead') {
        const storedValues =
          safeParse(localStorage.getItem('formValues'), {}) || {};
        const email =
          storedValues?.email ||
          storedValues?.EmailAddress ||
          authSession?.email ||
          '';

        if (email && email.trim()) {
          if (otpSubmitting) return;
          try {
            setOtpSubmitting(true);
            setOtpError('');
            await sendOtpToEmail(email);
          } finally {
            setOtpSubmitting(false);
          }
          return;
        }

        // No known email — ask the user to enter one
        setOtpModalData(buildRequestOtpModal(firstNameHint, { via: 'email' }));
        setModalStage('request-email');
        setOtpModalOpen(true);
        return;
      }

      if (id === 'use-phone-instead') {
        if (!ENABLE_OTP_SMS) return;
        setOtpModalData(buildRequestOtpModal(firstNameHint));
        setModalStage('request');
        setOtpModalOpen(true);
        return;
      }

      if (id === 'no-thats-not-me') {
        clearPersistedAuthSession();
        localStorage.clear();
        sessionStorage.clear();
        setOtpModalOpen(false);
        setModalStage('idle');
        setOtpViaEmail(false);
        window.location.href = '/';
        return;
      }

      if (id === 'guest-quote') {
        setOtpModalOpen(false);
        setModalStage('idle');
        setOtpViaEmail(false);
        return;
      }
    },
    [
      authSession?.cellNumber,
      authSession?.email,
      cellFromForm,
      firstNameHint,
      modalStage,
      otpSubmitting,
      sendOtpTo,
      sendOtpToEmail,
    ]
  );

  const handleOtpModalComplete = useCallback(
    async (formState) => {
      if (modalStage !== 'enter') return;
      if (formState?.otp_code?.length < 4) return;

      // inside your onComplete handler for the OTP modal
      const otp = (formState?.otp_code ?? '').toString().trim();

      // Prefer the persisted identifier used during Send OTP
      const session = safeParse(localStorage.getItem('authSession'), {}) || {};
      const cell =
        session.cellNumber ||
        formValues?.cellNumber ||
        cellFromForm ||
        (formState?.contact_number || '').replace(/\D/g, '');
      const email =
        session.email || formValues?.email || formValues?.EmailAddress || '';

      if (
        !/^\d{4}$/.test(otp) ||
        (!otpViaEmail && !cell) ||
        (otpViaEmail && !email)
      ) {
        return;
      }

      if (otpSubmitting) return;
      try {
        setOtpError('');
        setOtpSubmitting(true);
        const ok = otpViaEmail
          ? await tryGetReturnUserDetailsByEmail(email, otp)
          : await tryGetReturnUserDetails(cell, otp);
        if (ok) {
          // Success: close modal
          setOtpModalOpen(false);
          setModalStage('idle');
          setOtpViaEmail(false);
        } else {
          // Failure: keep modal open; maybe show a toast
        }
      } catch (e) {
        // Failure path already resets the modal back to request stage in tryGetReturnUserDetails
      } finally {
        setOtpSubmitting(false);
      }
    },
    [
      modalStage,
      cellFromForm,
      tryGetReturnUserDetails,
      tryGetReturnUserDetailsByEmail,
      otpSubmitting,
      otpViaEmail,
    ]
  );

  // ---------------- boot-time check ----------------
  useEffect(() => {
    if (!ENABLE_OTP_SMS) return;

    // Must have an ID in formValues
    const hasId =
      !!formValues && (!!formValues?.idNumber || !!formValues?.IdNumber);

    // Only use the identifier from a prior auth session — not from formValues.
    // prefillFromID can populate formValues.phone from a bureau lookup, which
    // should not be enough to trigger the OTP prompt on the home page.
    //
    // Use whichever channel the user actually authenticated with — a user who
    // signed in via email may not have a cellNumber on the session at all.
    const rawAuthVia = authSession?.otpVia === 'email' ? 'email' : 'phone';
    // SMS is disabled — always fall back to the email flow, even for a
    // session that previously authenticated via phone.
    const authVia = ENABLE_OTP_SMS ? rawAuthVia : 'email';
    const identifier =
      authVia === 'email'
        ? authSession?.email || ''
        : authSession?.cellNumber || '';
    const hasIdentifier = !!identifier;

    // Require the user to have previously completed or explicitly skipped the
    // OTP flow. A session with only cellNumber + requestedAt means an OTP was
    // sent but the user abandoned mid-flow — don't re-prompt them on return.
    const hasPriorSession = !!(
      authSession?.validatedAt || authSession?.bypassedAt
    );

    if (!hasId || !hasIdentifier || !hasPriorSession) return;

    // If the user validated their OTP within the last 8 hours, skip the OTP
    // modal entirely and silently re-fetch their data.
    if (isValidatedWithin()) {
      fetchReturnUserDataSilently(identifier, { via: authVia });
      return;
    }

    // If the user explicitly skipped OTP validation within the last 8 hours
    // (e.g. chose "Quote without signing in" in the question flow), don't
    // prompt them again on the homepage — but don't fetch their data either.
    if (isBypassedWithin()) {
      return;
    }

    // Validation window expired — show "Is that you?" without pre-filling the identifier.
    setOtpModalData(
      buildRequestOtpModal(firstNameHint, {
        via: authVia,
        returningUser: true,
      })
    );
    setModalStage(authVia === 'email' ? 'request-email' : 'request');
    setOtpModalOpen(true);
  }, [
    formValues,
    authSession?.cellNumber,
    authSession?.email,
    authSession?.otpVia,
    authSession?.validatedAt,
    authSession?.bypassedAt,
    cellFromForm,
    firstNameHint,
    fetchReturnUserDataSilently,
  ]);

  useEffect(() => {
    const handleJwtExpired = () => {
      if (!ENABLE_OTP_SMS) return;
      const session = safeParse(localStorage.getItem('authSession'), {}) || {};
      const rawExpiredVia = session?.otpVia === 'email' ? 'email' : 'phone';
      // SMS is disabled — always fall back to the email flow.
      const expiredVia = ENABLE_OTP_SMS ? rawExpiredVia : 'email';

      setOtpModalData(
        buildRequestOtpModal(firstNameHint, {
          via: expiredVia,
          returningUser: true,
        })
      );
      setModalStage(expiredVia === 'email' ? 'request-email' : 'request');
      setOtpModalOpen(true);
    };
    window.addEventListener('hippo:jwt-expired', handleJwtExpired);
    return () =>
      window.removeEventListener('hippo:jwt-expired', handleJwtExpired);
  }, [firstNameHint]);

  const handleHeroImageReady = useCallback(() => {
    setLoadedHeroKey(heroGateKey);
  }, [heroGateKey]);

  const isHeroImageReady = skipHeroGate || loadedHeroKey === heroGateKey;

  const sharedPageProps = {
    pathName: normalizedPathname,
    heroGateKey,
    onHeroImageLoad: handleHeroImageReady,
  };

  // ---------------- render ----------------

  return (
    <>
      {SeoTagsManager.render(pathname)}
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          pt: { xs: 2, sm: 2 },
          pb: 0,
          minHeight: '100vh',
          overflowX: 'clip', // or 'hidden'
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            backgroundColor: '#F3F6F6',
            zIndex: 0,
          },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
          }}
        >
          <PageShell
            containerSx={
              !showHomePage && BREADCRUMBS[normalizedPathname]
                ? { mt: isMobile.isDesktop ? '8px' : '0px' }
                : {}
            }
          >
            {!showHomePage && BREADCRUMBS[normalizedPathname] && (
              <Box
                sx={{
                  maxWidth: '1113px',
                  // mx: '0px',
                  pt: !isMobile.isDesktop ? '8px' : '0px',
                  pb: !isMobile.isDesktop ? '8px' : '0px',
                  pl: { xs: '16px', md: '24px' },
                }}
              >
                <BreadcrumbComponent
                  items={getBreadcrumbs(normalizedPathname)}
                />
              </Box>
            )}
            <Box
              sx={{
                visibility: isHeroImageReady ? 'visible' : 'hidden',
                pointerEvents: isHeroImageReady ? 'auto' : 'none',
                mt:
                  !showHomePage && BREADCRUMBS[normalizedPathname]
                    ? isMobile.isDesktop
                      ? '-16px'
                      : '16px'
                    : 0,
              }}
            >
              {showHomePage && <HomePage {...sharedPageProps} />}

              {showCarInsurancePage && (
                <CarInsurancePage {...sharedPageProps} />
              )}

              {showFuneralInsurancePage && (
                <FuneralInsurancePage {...sharedPageProps} />
              )}

              {showLifeInsurancePage && (
                <LifeInsurancePage {...sharedPageProps} />
              )}

              {showMedicalAidPage && <MedicalAidPage {...sharedPageProps} />}

              {showLifeInsuranceCompaniesPage && (
                <LifeInsuranceCompaniesPage {...sharedPageProps} />
              )}

              {showCarInsuranceCompaniesPage && (
                <CarInsuranceCompaniesPage {...sharedPageProps} />
              )}

              {showMedicalAidCompaniesPage && (
                <MedicalAidCompaniesPage {...sharedPageProps} />
              )}

              {showPetInsurancePage && (
                <PetInsurancePage {...sharedPageProps} />
              )}

              {showMotorWarranty && <MotorWarrantyPage {...sharedPageProps} />}

              {showDentsAndScratches && (
                <DentsAndScratchesPage {...sharedPageProps} />
              )}

              {showTravelInsurance && (
                <TravelInsurancePage {...sharedPageProps} />
              )}

              {showGapCover && <MedicalGapCoverPage {...sharedPageProps} />}

              {showMedicalInsurance && (
                <MedicalInsurancePage {...sharedPageProps} />
              )}

              {showBuildingInsurance && (
                <BuildingInsurancePage {...sharedPageProps} />
              )}

              {showBusinessInsurance && (
                <BusinessInsurancePage {...sharedPageProps} />
              )}

              {showTyreAndRim && <TyreAndRimPage {...sharedPageProps} />}

              {showVehicleTracking && (
                <VehicleTrackingPage {...sharedPageProps} />
              )}

              {showServicePlans && <ServicePlansPage {...sharedPageProps} />}

              {showFibre && <FibrePage {...sharedPageProps} />}

              {showMotorCycle && <MotorCyclePage {...sharedPageProps} />}

              {showHomeContent && <HomeContentPage {...sharedPageProps} />}

              {showComboInsurancePage && (
                <ComboInsurancePage {...sharedPageProps} />
              )}

              {showPersonalLoan && <PersonalLoanPage {...sharedPageProps} />}

              {showDebtCounselling && (
                <DebtCounsellingPage {...sharedPageProps} />
              )}

              {showDebtConsolidation && (
                <DebtConsolidationPage {...sharedPageProps} />
              )}

              {showCommercialProperty && (
                <CommercialPropertyPage {...sharedPageProps} />
              )}

              {showEmployeeBenefits && (
                <EmployeeBenefitPage {...sharedPageProps} />
              )}

              {showBusinessLegalCover && (
                <BusinessLegalCoverPage {...sharedPageProps} />
              )}

              {showProfessionalLiability && (
                <ProfessionalLiabilityPage {...sharedPageProps} />
              )}

              {showEventLiabilityCover && (
                <EventLiabilityCoverPage {...sharedPageProps} />
              )}

              {showInsuranceCategory && <InsurancePage {...sharedPageProps} />}

              {showMotorCategory && <MotorPage {...sharedPageProps} />}

              {showHealthCategory && <HealthPage {...sharedPageProps} />}

              {showMoneyCategory && <MoneyPage {...sharedPageProps} />}

              {showInternetCategory && <InternetPage {...sharedPageProps} />}
            </Box>
          </PageShell>

          <Box sx={{ mt: 'auto' }}>
            {isHeroImageReady ? (
              isMobile.isDesktop ? (
                <FooterComponent />
              ) : (
                <FooterMobileComponent />
              )
            ) : null}
          </Box>

          {/* OTP Modals */}
          {otpModalOpen && otpModalData && (
            <PopupModalComponent
              data={otpModalData}
              open={otpModalOpen}
              onClose={handleOtpModalClose}
              onAction={handleOtpModalAction}
              onComplete={handleOtpModalComplete}
              isMobile={!isMobile.isDesktop}
              loading={otpSubmitting}
              otpError={otpError}
              otpResetCounter={otpResetCounter}
            />
          )}
        </Box>

        {!isHeroImageReady ? (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#F3F6F6',
              zIndex: 2,
            }}
          >
            <LoadingDots variant="otp" />
          </Box>
        ) : null}
      </Box>
    </>
  );
}

export default Home;
